import React from 'react';
import ComponentBox from 'components/BusinessLogic/Components/ComponentBox';
import { faBuilding } from '@fortawesome/free-solid-svg-icons';
import PropTypes from 'prop-types';
import currencyFormat from 'utils/currency-format';
import Exchange from 'components/UI/Exchange';
import Guests from 'components/UI/Guests';
import ComponentSummary from 'components/BusinessLogic/Components/ComponentSummary';
import HotelRoomPictures from 'components/BusinessLogic/HotelRoomPictures';

function Hotel({
    hotel, dateLabel, showCommission, requestQuoteComponentId, requestQuoteId,
})
{
    const getCommission = (room, type) => {
        const commissionMap = {
            'Direct': () => parseFloat(room.direct_travel_agent_commission_amount || 0),
            'Commission Tracking': () => parseFloat(room.commission_tracking_travel_agent_commissionable_amount || 0),
            'Mixed': () => parseFloat(room.direct_travel_agent_commission_amount || 0) + parseFloat(room.commission_tracking_travel_agent_commissionable_amount || 0),
        };

        const commissionFn = commissionMap[type];

        if (typeof commissionFn === 'function')
        {
            return commissionFn();
        }

        return 0;
    };

    const totals = hotel.requestHotelRooms?.reduce((a, room) => ({
        commission: a.commission + getCommission(room, hotel.type),
        price: a.price + (room.total_price || 0),
    }), { price: 0, commission: 0 });

    return (
        <ComponentBox
            typeName="Hotel"
            requestQuoteComponentId={requestQuoteComponentId}
            requestQuoteId={requestQuoteId}
            description={hotel.name}
            icon={faBuilding}
            date={dateLabel}
            showViewMore={hotel.requestHotelRooms.length > 1}
            summaryContent={(
                <ComponentSummary
                    showCommission={showCommission}
                    currency={hotel.currency}
                    total={totals?.price ?? 0}
                    commission={totals?.commission ?? 0}
                />
            )}
        >
            <div className="tw-px-3 tw-w-full tw-py-1 tw-min-h-[250px] dark:tw-bg-zinc-800" style={{ height: (hotel.requestHotelRooms.length > 1 ? 'auto' : 195) }}>
                <div className="tw-flex tw-flex-row tw-flex-wrap">
                    {hotel.requestHotelRooms && hotel.requestHotelRooms.length > 0
                        && (hotel.requestHotelRooms).map((room, index) => (
                            <div key={room.id} className={`tw-pb-2 tw-mb-2 tw-flex tw-flex-row tw-flex-wrap ${index + 1 !== hotel.requestHotelRooms.length ? ' tw-border-b-2 tw-border-primary-default' : ''}`}>
                                {hotel.requestHotelRooms.length === 1 && (
                                    <span className="tw-w-full"><b>Suite</b>: {room.room_name}</span>
                                )}
                                {hotel.requestHotelRooms.length > 1 && (
                                    <>
                                        <span className="tw-w-1/2"><b>Suite</b>: {room.room_name}</span>
                                        <span className="tw-w-1/2 tw-text-right tw-text-xs">{(index + 1)} of {hotel.requestHotelRooms.length}</span>
                                    </>
                                )}
                                <span className="tw-w-1/2"><b>Checkin:</b> {room.checkin}</span>
                                <span className="tw-w-1/2"><b>Checkout:</b> {room.checkout}</span>

                                <span className="tw-w-1/2"><b>Promotion:</b> {room.hotelPromotion ? room.hotelPromotion.code : ''}</span>
                                <span className="tw-w-1/2">
                                    <b>Guests: </b>
                                    <Guests
                                        childrenAndAges={room.number_of_children_and_ages}
                                        adults={room.number_of_adults}
                                        infantsAndAges={room.number_of_infants_and_ages}
                                    />
                                </span>

                                <span className="tw-w-1/2"><b>Tax:</b> {room.tax}% </span>
                                {!(room.discount === 0) && (
                                    <span className="tw-w-1/2"><b>Discount:</b> {currencyFormat(room.discount, 2, room.currency ? room.currency.code : null)}</span>
                                )}

                                <span className="tw-w-1/2"><b>Night: </b>{room.nights} </span>
                                {showCommission
                                && <span className="tw-w-1/2"><b>Commission: </b>{currencyFormat(getCommission(room, hotel.type))} </span>}

                                <span className="tw-w-full"><b>T. Price: </b>
                                    <Exchange
                                        price={room.total_price}
                                        currency={room.currency}
                                    />
                                </span>
                                <HotelRoomPictures requestRoom={room} />
                            </div>
                        ))}

                </div>
            </div>
        </ComponentBox>
    );
}

Hotel.defaultProps = {
    dateLabel: null,
    showCommission: true,
};

Hotel.propTypes = {
    requestQuoteComponentId: PropTypes.number,
    requestQuoteId: PropTypes.number,
    dateLabel: PropTypes.string,
    showCommission: PropTypes.bool,
    hotel: PropTypes.shape(
        {
            currency: PropTypes.shape({
                currency_change: PropTypes.number,
                code: PropTypes.string,
            }),
            type: PropTypes.string,
            name: PropTypes.string,
            requestHotelRooms: PropTypes.arrayOf(PropTypes.shape(
                {
                    hotelPromotion: PropTypes.shape({
                        code: PropTypes.string,
                    }),
                    commission_tracking_travel_agent_commissionable_amount: PropTypes.any,
                    direct_travel_agent_commission_amount: PropTypes.any,
                    number_of_adults: PropTypes.number,
                    number_of_infants_and_ages: PropTypes.array,
                    number_of_children_and_ages: PropTypes.array,
                    total_price: PropTypes.number,
                    currency: PropTypes.shape({
                        currency_change: PropTypes.number,
                        code: PropTypes.string,
                    }),
                    checkin: PropTypes.string,
                    checkout: PropTypes.string,
                    room_name: PropTypes.string,
                    tax: PropTypes.number,
                    nights: PropTypes.number,
                    discount: PropTypes.number,
                },
            )),
        },
    ),
};
export default Hotel;
